import * as React from 'react';
import styled from 'styled-components';
import {Container as LastTransactionsContainer, Header, Title} from './LastTransactions';
import StatisticSummary from './StatisticSummary';
import Checkbox from 'components/Checkbox';
import {Content, Graph, GraphBox, GraphFilters, Statistic} from './Students';
import {transparentize} from 'polished';
import {DashboardProps} from '../index';
import {getCurrencyChar} from 'helpers/get-price-label';
import {transition} from 'helpers/css';
import {Currency} from 'types';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import GraphTooltip, {GraphTooltipItem} from './GraphTooltip';

export type TransactionsProps = Pick<DashboardProps, 'transactions'>

export enum LineKey {
  All='all',
  New='new',
  Repeated='repeated',
}

export enum Tabs {
  Receipts= 'receipts',
  Transactions= 'transactionsCount'
}

const Container = styled(LastTransactionsContainer)`
  max-width: none;
  
  ${Header} {
    display: grid;
    grid-template-columns: repeat(2, max-content);
    gap: 15px;
  }
`;

const Tab = styled(Title)<{active?: boolean}>`
  cursor: pointer;
  color: ${({active, theme}) => (
    !active && transparentize(.6, theme.colors.white))};
  
  ${transition()}
`;

/**
 * The function render transactions
 * @param {TransactionsProps} props
 * @constructor
 */
export default function Transactions({transactions}: TransactionsProps) {
  const currency = getCurrencyChar(Currency.USD);

  const [currentActive, setCurrentActive] = React.useState('all');

  const [activeTab, setActiveTab] = React.useState(Tabs.Receipts);

  const colors = {
    all: '#3264EF',
    new: '#93FFD1',
    repeated: '#73CDFF',
  };

  const [showedLines, setShowedLines] = React.useState({
    all: true,
    new: true,
    repeated: true,
  });

  /**
   * The function set showed lines
   * @param {string} key
   * @param {boolean} checked
   * @constructor
   */
  function changeShowedLines(key: LineKey, checked: boolean) {
    setShowedLines({
      ...showedLines,
      [key]: checked,
    });
  }

  const data = [
    {
      name: 'Янв',
      all: 12,
      new: 12,
      repeated: 0,
    },
    {
      name: 'Фев',
      all: 30,
      new: 30,
      repeated: 0,
    },
    {
      name: 'Мар',
      all: 320,
      new: 125,
      repeated: 215,
    },
    {
      name: 'Апр',
      all: 290,
      new: 10,
      repeated: 200,
    },
    {
      name: 'Май',
      all: 320,
      new: 25,
      repeated: 270,
    },
    {
      name: 'Июн',
      all: 400,
      new: 50,
      repeated: 300,
    },
    {
      name: 'Июл',
      all: 430,
      new: 59,
      repeated: 330,
    },
    {
      name: 'Авг',
      all: 450,
      new: 10,
      repeated: 400,
    },
    {
      name: 'Сен',
      all: 370,
      new: 12,
      repeated: 288,
    },
    {
      name: 'Окт',
      all: 370,
      new: 12,
      repeated: 288,
    },
    {
      name: 'Ноя',
      all: 370,
      new: 12,
      repeated: 288,
    },
    {
      name: 'Дек',
      all: 370,
      new: 12,
      repeated: 288,
    },
  ];

  /**
   * The function set showed lines
   * @param {TooltipProps} props
   * @constructor
   */
  function CustomTooltip(props: TooltipProps<any, any>): JSX.Element | null {
    if (props.active && props?.payload) {
      const items: GraphTooltipItem[] = [];

      showedLines.all && items.push({
        title: 'Все',
        dotColor: colors.all,
        value: `${props.payload[0].payload.all} ${currency}`,
      });
      showedLines.new && items.push({
        title: 'Новые',
        dotColor: colors.new,
        value: `${props.payload[0].payload.new} ${currency}`,
      });
      showedLines.repeated && items.push({
        title: 'Повторные',
        dotColor: colors.repeated,
        value: `${props.payload[0].payload.repeated} ${currency}`,
      });


      return (
        <GraphTooltip
          header={props.label}
          items={items}
        />
      );
    }

    return null;
  }

  return (
    <Container>
      <Header>
        <Tab
          active={activeTab === Tabs.Receipts}
          onClick={() => setActiveTab(Tabs.Receipts)}
        >Поступления</Tab>
        <Tab
          active={activeTab === Tabs.Transactions}
          onClick={() => setActiveTab(Tabs.Transactions)}
        >Количество транзакций</Tab>
      </Header>
      <Content>
        <GraphBox>
          <Graph>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
              >
                <CartesianGrid vertical={false} opacity={0.1} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip content={<CustomTooltip />}/>
                <Line
                  type="monotone"
                  dataKey={LineKey.All}
                  stroke={colors.all}
                  fill={colors.all}
                  hide={!showedLines.all}
                  strokeWidth={3}
                />
                <Line
                  type="monotone"
                  dataKey={LineKey.New}
                  stroke={colors.new}
                  fill={colors.new}
                  hide={!showedLines.new}
                  strokeWidth={3}
                />
                <Line
                  type="monotone"
                  dataKey={LineKey.Repeated}
                  stroke={colors.repeated}
                  fill={colors.repeated}
                  hide={!showedLines.repeated}
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          </Graph>
          <GraphFilters>
            <Checkbox
              label={'все'}
              accentLabel
              colorize={'#3264EF'}
              checked={showedLines.all}
              onChange={(e) => changeShowedLines(
                  LineKey.All,
                  e.target.checked,
              )}
            />
            <Checkbox
              label={'новые'}
              accentLabel
              colorize={colors.new}
              checked={showedLines.new}
              onChange={(e) => changeShowedLines(
                  LineKey.New,
                  e.target.checked,
              )}
            />
            <Checkbox
              label={'Повторные'}
              accentLabel
              colorize={colors.repeated}
              checked={showedLines.repeated}
              onChange={(e) => changeShowedLines(
                  LineKey.Repeated,
                  e.target.checked,
              )}
            />
          </GraphFilters>
        </GraphBox>
        <Statistic>
          <StatisticSummary
            title={'сегодня'}
            value={`${transactions.today} ${currency}`}
            onClick={() => setCurrentActive('today')}
            primary={currentActive === 'today'}
          />
          <StatisticSummary
            title={'в этом месяце'}
            value={`${transactions.month} ${currency}`}
            onClick={() => setCurrentActive('month')}
            primary={currentActive === 'month'}
          />
          <StatisticSummary
            title={'за все время'}
            value={`${transactions.all} ${currency}`}
            onClick={() => setCurrentActive('all')}
            primary={currentActive === 'all'}
          />
        </Statistic>
      </Content>
    </Container>
  );
}
